// Sticky filter bar on collection and search pages -- SG 20261019
// The header keeps its site-wide behaviour (the bubble stays, the announcement bar hides on
// scroll down and returns on scroll up); the bar pins flush against the bubble's bottom edge.
// Once pinned it merges into the bubble. Where scroll-driven animations exist the CSS runs the
// merge from scroll position alone; elsewhere .facets-bar-merged on <html> drives it. -- SG 20261026

const FOLLOW_DURATION = 350;
// Matches .section-header.sticky-header.with-announcement-bar (top-10).
const ANNOUNCEMENT_HEIGHT = 40;
const MERGED_CLASS = "facets-bar-merged";
// Set by header-announcement.ts within a screen of the page's end -- SG 20261027
const HEADER_SETTLED_CLASS = "header-settled";
const SETTLED_CLASS = "is-settled";
// Height of .collection__facets-sentinel, the scroll distance the wrap plays over -- SG 20261027
const MERGE_DISTANCE = 64;
const SCROLL_DRIVEN =
  typeof CSS !== "undefined" && CSS.supports("animation-timeline: scroll()") && CSS.supports("timeline-scope: --a");
// Room above a focused item for the header and bar (WCAG 2.4.11), see facets-general.css -- SG 20261020
const FOCUS_CLEARANCE = 64;

export class FacetBarScroll {
  private header = document.querySelector<HTMLElement>(".section-header");
  private bubble = this.header?.querySelector<HTMLElement>(".header") || null;
  private section: HTMLElement | null = null;
  private reachBox: HTMLElement | null = null;
  private reach = 0;
  private scrollbar = -1;
  private abortController = new AbortController();
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private frameRequested = false;
  private followUntil = 0;
  private following = false;
  private barTop = 0;
  private merged = false;
  private settled = false;

  constructor(private bar: HTMLElement, private sentinel: HTMLElement) {}

  start() {
    const opts = { signal: this.abortController.signal };
    // collection-main is inline, so the section is the bar's sticky containing block -- SG 20261026
    this.section = this.bar.closest<HTMLElement>(".shopify-section");
    if (this.section) {
      this.reachBox = document.createElement("div");
      this.reachBox.className = "collection__facets-reach";
      this.reachBox.setAttribute("aria-hidden", "true");
      this.section.append(this.reachBox);
    }
    // The header is only sticky from the first paint when header-announcement is on the page -- SG 20261026
    if (!SCROLL_DRIVEN || !document.querySelector("header-announcement")) {
      window.addEventListener("scroll", () => this.requestFrame(), { passive: true, ...opts });
    }
    window.addEventListener("resize", () => this.measure(), opts);

    if (this.header && typeof ResizeObserver === "function") {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.header);
      // The page grows as products load and images arrive -- SG 20261026
      this.resizeObserver.observe(document.body);
    }

    // The announcement bar toggles header classes that animate its top offset -- SG 20261020
    if (this.header) {
      this.mutationObserver = new MutationObserver(() => {
        this.followHeader();
        this.syncSettled();
      });
      this.mutationObserver.observe(this.header, { attributes: true, attributeFilter: ["class"] });
    }

    this.measure();
  }

  stop() {
    this.abortController.abort();
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    document.documentElement.style.removeProperty("scroll-padding-top");
    document.documentElement.classList.remove(MERGED_CLASS);
    this.bar.classList.remove(SETTLED_CLASS);
    this.sentinel.classList.remove(SETTLED_CLASS);
    this.header?.classList.remove("facets-settled");
    this.reachBox?.remove();
    this.section?.style.removeProperty("margin-bottom");
  }

  // After Show results, bring the top of the grid into view. Scrolling up brings the header
  // (and announcement bar) back, so leave room for both above the bar.
  scrollToResults() {
    const headerRoom = ANNOUNCEMENT_HEIGHT + (this.bubble ? this.bubble.offsetHeight : 0);
    const sentinelTop = this.sentinel.getBoundingClientRect().top;
    if (sentinelTop >= headerRoom) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Land past the merge distance so the bar rests fully merged; mb-6 below the bar keeps the
    // first row clear. Whole pixels avoid a hairline gap. -- SG 20261026
    const top = Math.ceil(window.scrollY + sentinelTop - headerRoom) + MERGE_DISTANCE;
    // "auto" would inherit html { scroll-behavior: smooth }, so jump outright under reduced motion -- EZ 20261028
    window.scrollTo({ top, behavior: reduceMotion ? ("instant" as unknown as ScrollBehavior) : "smooth" });
  }

  private requestFrame() {
    if (this.frameRequested) return;
    this.frameRequested = true;
    window.requestAnimationFrame(() => {
      this.frameRequested = false;
      this.syncBarTop();
    });
  }

  // The header animates its top offset; track its edge each frame so the bar never gaps.
  // Every header class mutation extends the window; only one frame chain runs at a time -- EZ 20261028
  private followHeader() {
    this.followUntil = performance.now() + FOLLOW_DURATION;
    if (this.following) return;
    this.following = true;
    const step = () => {
      this.syncBarTop();
      if (performance.now() < this.followUntil) window.requestAnimationFrame(step);
      else this.following = false;
    };
    window.requestAnimationFrame(step);
  }

  // The bar's panel and alignment are sized in CSS from the viewport width, which includes a
  // classic scrollbar, so publish its width. Runs on resize and header size changes only. -- SG 20261026
  // Custom properties go on the elements that read them, never <html>: a change there restyles
  // every element, about 110ms on a full collection page in Safari. -- SG 20261027
  private measure() {
    const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    if (scrollbar !== this.scrollbar) {
      this.scrollbar = scrollbar;
      this.bar.style.setProperty("--scrollbar-width", `${scrollbar}px`);
    }
    this.measureReach();
    this.syncBarTop();
  }

  // The bar can only stick within the section, so it would be left behind at the end of the grid
  // while the bubble floats on over the footer. An empty box reaches the section to the end of the
  // page and a negative margin hands that height back, so nothing after it moves. Both are inline
  // sizes, so a change only reflows. Measured against the body, which keeps its height while the
  // menu's scroll lock fixes it in place. Rounded down, so it never adds scroll room. -- SG 20261026
  private measureReach() {
    if (!this.section || !this.reachBox) return;
    const sectionBottom = this.section.getBoundingClientRect().bottom - this.reach;
    const reach = Math.max(0, Math.floor(document.body.getBoundingClientRect().bottom - sectionBottom));
    if (reach !== this.reach) {
      this.reach = reach;
      this.reachBox.style.height = `${reach}px`;
      this.section.style.marginBottom = `${-reach}px`;
    }
  }

  private syncBarTop() {
    // Mid-bounce on iOS the bubble is wherever the bounce has carried it, so keep the last edge -- SG 20261027
    const scrollY = window.scrollY;
    if (scrollY < 0 || scrollY > document.documentElement.scrollHeight - window.innerHeight + 1) return;
    const bubbleBottom = this.bubble ? this.bubble.getBoundingClientRect().bottom : 0;
    const top = Math.max(0, Math.round(bubbleBottom));
    if (top !== this.barTop) {
      this.barTop = top;
      this.bar.style.setProperty("--facets-bar-top", `${top}px`);
      this.sentinel.style.setProperty("--facets-bar-top", `${top}px`);
      document.documentElement.style.scrollPaddingTop = `${top + FOCUS_CLEARANCE}px`;
    }

    if (SCROLL_DRIVEN) return;
    // Pinned means the sentinel has scrolled up past the bar's sticky offset under a floating bubble.
    const floating = !!this.bubble && !!this.header?.classList.contains("sticky-header");
    const merged = floating && this.sentinel.getBoundingClientRect().top < top + 1;
    if (merged !== this.merged) {
      this.merged = merged;
      document.documentElement.classList.toggle(MERGED_CLASS, merged);
    }
  }

  // Once the header settles near the page's end and the wrap has finished (the sentinel is past
  // its 64px), the wrap's scroll-driven animations come off too and the class driver holds the
  // bar merged, for the same iOS rubber-band fault (header-announcement.ts). The sentinel is
  // only read while the header is settled. -- SG 20261027
  private syncSettled() {
    if (!SCROLL_DRIVEN) return;
    const headerSettled = !!this.header?.classList.contains(HEADER_SETTLED_CLASS);
    if (headerSettled === this.settled) return;
    const settled = headerSettled && this.sentinel.getBoundingClientRect().top < this.barTop - MERGE_DISTANCE - 1;
    if (settled === this.settled) return;
    this.settled = settled;
    this.bar.classList.toggle(SETTLED_CLASS, settled);
    this.sentinel.classList.toggle(SETTLED_CLASS, settled);
    this.header?.classList.toggle("facets-settled", settled);
  }
}
